import React, { FC, useMemo, useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { AddNewReportFormCont, FormCellCont, FormElement, Label } from './AddNewReportForm.styles';
import { IPostReportingGroupBody } from '../../../services/reports';
import { IReportsSetupItem } from '../../../services/surveys';
import SearchDropdown from '../../SearchDropdown/SearchDropdown';
import SearchInputHeader from '../../SearchDropdown/SearchInputHeader/SearchInputHeader';

interface IAddNewReportForm {
  submit: (data: IPostReportingGroupBody) => void;
  apiError?: { [key: string]: string };
  editData?: IReportsSetupItem;
  isEffectSurvey: boolean;
}

const AddNewReportForm: FC<IAddNewReportForm> = ({
  submit,
  apiError,
  editData,
  isEffectSurvey,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef();

  const typeOptions = useMemo(
    () => [
      { id: 1, name: t('viewSurvey.reportsSetup.teamReports'), icon: '', shortDisplayName: 'team' },
      {
        id: 2,
        name: t('viewSurvey.reportsSetup.executiveReports'),
        icon: '',
        shortDisplayName: 'executive',
      },
    ],
    [t]
  );

  const addReportSchema = object({
    name: string()
      .required(t('viewSurvey.reportsSetup.modals.validations.nameIsRequired'))
      .matches(
        /^[^&$@=;/:+,?+\\{}^%`[\]~"'<>#|]*$/,
        t('viewSurvey.reportsSetup.modals.validations.nameForbidenChars')
      ),
    type: object().nullable(),
  });

  const defaultValues = {
    name: editData?.name || '',
    type: editData?.type
      ? typeOptions.find(({ shortDisplayName }) => shortDisplayName === editData.type)
      : typeOptions[0],
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(addReportSchema),
  });

  const saveForm = (data: typeof defaultValues) => {
    const finalVal = { ...data, ...{ type: data?.type?.shortDisplayName || '' } };
    submit(finalVal);
  };
  return (
    <AddNewReportFormCont
      id="AddNewReportFormCont"
      onSubmit={handleSubmit(saveForm)}
      isEffectSurvey={isEffectSurvey}
    >
      {isEffectSurvey ? null : (
        <FormElement id="FormElement">
          <Label id="label">{t('reports.type')}</Label>
          <FormCellCont id="FormCellCont">
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <SearchDropdown
                  items={typeOptions}
                  placeholder="type"
                  selectEl={onChange}
                  width="320px"
                  activeEl={value}
                  error={errors?.type?.message || apiError?.type}
                  selectFullObject
                  isDisabled={!!editData}
                />
              )}
              name="type"
            />
          </FormCellCont>
        </FormElement>
      )}

      <FormElement id="FormElement">
        <Label id="Label">{t('reports.name')}</Label>
        <FormCellCont id="FormCellCont">
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <SearchInputHeader
                width="320px"
                noDrop
                searchForList={onChange}
                active={false}
                placeholder={t('reports.name')}
                inputRef={inputRef}
                defaultValue={value}
                error={errors?.name?.message || apiError?.name}
              />
            )}
            name="name"
          />
        </FormCellCont>
      </FormElement>
      <button data-testid="submitAddReport" id="AddReportModalCallToAction" type="submit" hidden>
        callToAction
      </button>
    </AddNewReportFormCont>
  );
};

export default AddNewReportForm;
